// Auth client + in-memory CSRF token store.
//
// Login is Webex OAuth. The Control UI kicks it off by navigating
// the top-level document to ${syncBaseURL}/auth/webex/start; the
// sync server handles the round-trip with Webex and redirects back
// to CONTROL_UI_URL with the casper_sid cookie set.
//
// The CSRF token is delivered in the /auth/me response body (not
// read from cookies — the session cookie is on the sync origin, and
// the Control UI is a different origin so document.cookie can't see
// it). We stash it here and hand it to every write request.

import { loadConfig } from './config';

let csrfToken: string | null = null;

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function setCsrfToken(v: string | null) {
  csrfToken = v;
}

async function base() {
  return (await loadConfig()).syncBaseURL;
}

export interface AuthUser {
  id: number;
  email: string;
  display_name: string | null;
  role: 'admin' | 'editor' | 'viewer';
}

interface MeResponse {
  user: AuthUser;
  csrfToken: string;
}

export async function getMe(): Promise<AuthUser | null> {
  const res = await fetch(`${await base()}/auth/me`, { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`getMe HTTP ${res.status}`);
  const body = (await res.json()) as MeResponse;
  setCsrfToken(body.csrfToken ?? null);
  return body.user;
}

/** Full URL to kick off the Webex OAuth handshake. */
export async function webexLoginURL(): Promise<string> {
  return `${await base()}/auth/webex/start`;
}

/** Redirect the top-level document to Webex for sign-in. */
export async function startWebexLogin(): Promise<void> {
  window.location.assign(await webexLoginURL());
}

export async function logout(): Promise<void> {
  await fetch(`${await base()}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
  });
  setCsrfToken(null);
}
